import { useQueryStates } from 'nuqs'
import { useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import { ProductSearchContext, ProductSearchStaticContext } from '@Globals/Algolia/Context'
import { queryParameterEnumerable, queryParameterPage, queryParameterRange } from '@Globals/Algolia/Shared'
import { FacetDynamicConfigFacet, FacetDynamicConfigValue, FacetStats } from '@Globals/Algolia/Types'
import { gtagEvent } from '@Globals/Google/GTag'

type EnumerableReturnType = {
  checked: boolean
  count: number
  onChange: () => void
}

type GroupReturnType = {
  showGroupTitle: boolean
}

type RangeReturnType = {
  from?: number
  onChange?: (from?: number, to?: number) => void
  stats: FacetStats[string]
  to?: number
}

export function useEnumerable(parameter: string, value: string): EnumerableReturnType | null {
  const [queryStates, setQueryStates] = useQueryStates({
    page: queryParameterPage,
    [parameter]: queryParameterEnumerable,
  })
  const { facetDynamicConfig } = useContext(ProductSearchContext)

  const lookupKey = `${parameter}:${value}`
  const data = facetDynamicConfig[lookupKey] as FacetDynamicConfigValue | undefined
  const count = data?.count ?? 0
  const checked = (queryStates[parameter] as string[] | null)?.includes(value) || false

  return useMemo(() => ({
    checked,
    count,
    onChange: () => {
      setQueryStates((prev) => {
        let enabled = false
        let nextValue = prev[parameter] as string[] | null

        if (!nextValue) {
          enabled = true
          nextValue = [value]
        } else if (!nextValue.includes(value)) {
          enabled = true
          nextValue = [...nextValue, value].sort()
        } else if (nextValue.length > 1) {
          nextValue = nextValue.filter(v => v !== value).sort()
        } else {
          nextValue = null
        }

        if (enabled) {
          ampli.plApplyFilter({
            filter_code: parameter,
            filter_option_id: value,
          })
          gtagEvent('filter_changed', {
            filter_code: parameter,
            filter_option_id: value,
          })
        }

        return {
          ...prev,
          [parameter]: nextValue,
          page: null,
        }
      })
    },
  }), [checked, count, parameter, setQueryStates, value])
}

export function useGroup(parameter: string): GroupReturnType | null {
  const { facetDynamicConfig } = useContext(ProductSearchContext)

  const facetData = facetDynamicConfig[parameter] as FacetDynamicConfigFacet | undefined
  const showGroupTitle = facetData?.showGroupTitle ?? false

  return useMemo(() => ({
    showGroupTitle,
  }), [showGroupTitle])
}

export function useRange(parameter: string, key: string): RangeReturnType | null {
  // TODO: read from and to from query states, with actual min and max from facet config
  const [, setQueryStates] = useQueryStates({
    page: queryParameterPage,
    [parameter]: queryParameterRange,
  })
  const { initialFacetStats } = useContext(ProductSearchStaticContext)
  const { numericFilters } = useContext(ProductSearchContext)
  const numericFilter = numericFilters?.[key]
  const initialStats = initialFacetStats?.[key]

  return useMemo(() => {
    if (!initialStats) {
      return null
    }

    return {
      from: numericFilter?.min,
      onChange: (from?: number, to?: number) => {
        if (from === undefined || to === undefined) {
          setQueryStates(() => ({
            [parameter]: null,
            page: null,
          }))
          return
        }

        const value = [Math.min(from, to), Math.max(from, to)]

        ampli.plApplyFilter({
          filter_code: parameter,
          filter_option_id: value.join('-'),
        })
        gtagEvent('filter_changed', {
          filter_code: parameter,
          filter_option_id: value.join('-'),
        })

        setQueryStates(() => ({
          [parameter]: value,
          page: null,
        }))
      },
      stats: initialStats,
      to: numericFilter?.max,
    }
  }, [parameter, initialStats, numericFilter, setQueryStates])
}
